import type { PlanTask } from '@/types/domain';

export const DAY_MS = 86_400_000;

export interface GanttRange {
  start: Date;
  end: Date;
  days: number;
}

function validDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const [year, month, day] = value.slice(0, 10).split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return Number.isNaN(date.getTime()) ? null : date;
}

export function createGanttRange(tasks: PlanTask[]): GanttRange | null {
  const dates = tasks
    .flatMap((task) => [validDate(task.planStart), validDate(task.planEnd)])
    .filter((date): date is Date => date !== null);
  if (!dates.length) return null;
  const start = new Date(Math.min(...dates.map((date) => date.getTime())));
  const end = new Date(Math.max(...dates.map((date) => date.getTime())));
  return { start, end, days: Math.max(1, Math.round((end.getTime() - start.getTime()) / DAY_MS) + 1) };
}

export function barStyle(task: PlanTask, range: GanttRange): { left: string; width: string } | null {
  const start = validDate(task.planStart);
  const end = validDate(task.planEnd);
  if (!start || !end || end < start) return null;
  const offset = Math.max(0, Math.round((start.getTime() - range.start.getTime()) / DAY_MS));
  const duration = Math.max(1, Math.round((end.getTime() - start.getTime()) / DAY_MS) + 1);
  return {
    left: `${(offset / range.days) * 100}%`,
    width: `${Math.min(100 - (offset / range.days) * 100, (duration / range.days) * 100)}%`,
  };
}

export function actualStyle(task: PlanTask, range: GanttRange): { left: string; width: string } | null {
  const start = validDate(task.planStart);
  const actual = validDate(task.actualEnd);
  if (!start || !actual || actual < start) return null;
  return barStyle({ ...task, planEnd: task.actualEnd }, range);
}

export function todayPosition(range: GanttRange, today = new Date()): number | null {
  const normalized = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));
  if (normalized < range.start || normalized > range.end) return null;
  return ((normalized.getTime() - range.start.getTime()) / DAY_MS / range.days) * 100;
}

export function monthTicks(range: GanttRange): Array<{ label: string; position: number }> {
  const ticks: Array<{ label: string; position: number }> = [];
  const cursor = new Date(Date.UTC(range.start.getUTCFullYear(), range.start.getUTCMonth(), 1));
  if (cursor < range.start) cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  while (cursor <= range.end) {
    ticks.push({
      label: `${cursor.getUTCFullYear()}.${String(cursor.getUTCMonth() + 1).padStart(2, '0')}`,
      position: ((cursor.getTime() - range.start.getTime()) / DAY_MS / range.days) * 100,
    });
    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  }
  return ticks;
}

/**
 * 像素位移换算成天数。
 *
 * 甘特条是按百分比定位的，拖动时拿到的是像素。时间轴总宽对应 range.days 天，
 * 所以一天的宽度 = 轨道宽 / 总天数，位移除以它再取整就是天数。
 *
 * 取整用 round 而不是 floor：拖到某一天的中点之后就该算作那一天，
 * 否则手感上总差一格。
 */
export function pixelsToDays(deltaPx: number, trackWidthPx: number, range: GanttRange): number {
  if (trackWidthPx <= 0 || range.days <= 0) return 0;
  return Math.round((deltaPx / trackWidthPx) * range.days);
}

/** 日期串加减天数；纯 UTC 运算，避免时区把日期算飘 */
export function shiftDay(day: string, days: number): string {
  const base = Date.parse(`${day.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(base)) return day.slice(0, 10);
  return new Date(base + days * DAY_MS).toISOString().slice(0, 10);
}
